'use client';

import Link from 'next/link';
import posthog from 'posthog-js';
import { useId } from 'react';
import { useWhatsNewRailTargets } from '@/components/layout/whats-new/use-whats-new-rail-targets';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { useChromePathname } from '@/shared/components/providers/chrome-pathname';

const AI_HREF = '/ai';
const AI_TARGETS = [AI_HREF] as const;

/**
 * Taboo Terra entry in the platform header. Ungated: the beta is open to every
 * member. Took the Messages shortcut's slot; `HeaderMessagesShortcut` stays
 * mounted in the studio header. /ai has no rail item, so the What's New dot
 * for a `/ai…` announcement lands here.
 *
 * White pill so it stands out on the black header without competing with the
 * red Create pill beside it; the label only fits from `lg` up, icon-only below.
 * Hover (`.ai-entry-pill`, globals.css) keeps the face white — a grey fill read
 * as disabled.
 */
export function HeaderAiShortcut() {
  const pathname = useChromePathname();
  const isNew = useWhatsNewRailTargets(AI_TARGETS).has(AI_HREF);
  const isActive = pathname === AI_HREF;
  const gradientId = `terra-wand-${useId()}`;

  return (
    <Link
      href={AI_HREF}
      {...(isActive ? { 'aria-current': 'page' as const } : {})}
      onClick={() => {
        posthog.capture(AnalyticsEvent.AI_ENTRY_CLICKED, {
          entry_point: 'header',
          has_new_dot: isNew,
          from_path: window.location.pathname,
        });
      }}
      className="ai-entry-pill group m-0.5 flex size-8 shrink-0 items-center justify-center gap-2 rounded-full bg-white text-sm font-medium text-black focus-visible:outline-none sm:m-1 lg:m-0 lg:h-10 lg:w-auto lg:px-4"
      aria-label="Ask Taboo Terra"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 576 512"
        className="h-[18px] w-5 shrink-0 transition-transform duration-300 ease-[cubic-bezier(.34,1.56,.64,1)] group-hover:-rotate-[14deg] group-hover:scale-115 group-focus-visible:-rotate-[14deg] group-focus-visible:scale-115 motion-reduce:transform-none"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" style={{ stopColor: 'var(--red-hover)' }} />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
        <path
          fill={`url(#${gradientId})`}
          d="M234.7 42.7L197 56.8c-3 1.1-5 4-5 7.2s2 6.1 5 7.2l37.7 14.1L248.8 123c1.1 3 4 5 7.2 5s6.1-2 7.2-5l14.1-37.7L315 71.2c3-1.1 5-4 5-7.2s-2-6.1-5-7.2L277.3 42.7 263.2 5c-1.1-3-4-5-7.2-5s-6.1 2-7.2 5L234.7 42.7zM46.1 395.4c-18.7 18.7-18.7 49.1 0 67.9l34.6 34.6c18.7 18.7 49.1 18.7 67.9 0L529.9 116.5c18.7-18.7 18.7-49.1 0-67.9L495.3 14.1c-18.7-18.7-49.1-18.7-67.9 0L46.1 395.4zM484.6 82.6l-105 105-23.3-23.3 105-105 23.3 23.3zM7.5 117.2C3 118.9 0 123.2 0 128s3 9.1 7.5 10.8L64 160l21.2 56.5c1.7 4.5 6 7.5 10.8 7.5s9.1-3 10.8-7.5L128 160l56.5-21.2c4.5-1.7 7.5-6 7.5-10.8s-3-9.1-7.5-10.8L128 96 106.8 39.5C105.1 35 100.8 32 96 32s-9.1 3-10.8 7.5L64 96 7.5 117.2zm352 256c-4.5 1.7-7.5 6-7.5 10.8s3 9.1 7.5 10.8L416 416l21.2 56.5c1.7 4.5 6 7.5 10.8 7.5s9.1-3 10.8-7.5L480 416l56.5-21.2c4.5-1.7 7.5-6 7.5-10.8s-3-9.1-7.5-10.8L480 352l-21.2-56.5c-1.7-4.5-6-7.5-10.8-7.5s-9.1 3-10.8 7.5L416 352l-56.5 21.2z"
        />
      </svg>
      <span className="hidden whitespace-nowrap lg:inline">Ask Taboo Terra</span>
      {isNew && (
        <span className="absolute right-0 top-0 size-2.5 rounded-full border-2 border-black bg-red-primary">
          <span className="sr-only">New</span>
        </span>
      )}
    </Link>
  );
}
